import React, { useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { useStickyMaxHeight } from "../../hooks/useStickyMaxHeight";
import { Heading } from "../heading";
import { ACCENT, MONTHS_ES, formatMXN } from "../cuentas/shared";
import { getYearRange } from "../../utils/dates";
import { totalCostos } from "../../utils/costos";
import {
  resumenDinero,
  resumenEventos,
  resumenLeads,
  resumenInventario,
  resumenClientes,
  resumenPlanners,
  estructuraCostos,
  carteraPorCobrar,
  cajaPorMetodo,
  estacionalidadMensual,
  agendaVendida,
} from "../../utils/reportes";
import { buildOccupiedSet, rangoOcupacion } from "../../utils/ocupacion";
import {
  usePeriodo,
  PeriodoSelector,
  useDepositos,
  diaLocalDeAncla,
} from "./shared";
import { REPORTES, reportePorId, gruposConReportes } from "./registry";
import { useAuth } from "../AuthProvider";
import { esInversionista } from "../../utils/roles";
import { ChartBarIcon } from "@heroicons/react/20/solid";

// Una sola página: el índice de reportes a la izquierda (el mismo patrón
// que la edición del venue) y el reporte elegido a la derecha. Cambiar de
// reporte es un click, no una navegación — y la selección y el período
// viven en la URL, así que se comparte tal cual se ve.
//
// El índice reemplazó a una rejilla de tarjetas: con trece reportes, la
// rejilla ocupaba dos filas completas antes de que se viera un solo número,
// y cada reporte nuevo empujaba el contenido más abajo. El dato vivo de
// cada tarjeta sobrevivió como segunda línea de cada renglón.
const ReportesPage = () => {
  const {
    selectedVenue,
    events,
    payments,
    refunds,
    costos,
    retiros,
    contactForms,
    conceptos,
    clientes,
    proveedores,
    metodosPagoConDefaultsLista,
    calendarTimezone,
  } = useVenue();
  // La tarjeta de Flujo de caja muestra el MISMO flujo neto que el reporte
  // (con depósitos y retiros); si no, el número de la puerta no coincide
  // con el de adentro.
  const depositos = useDepositos();
  const [searchParams, setSearchParams] = useSearchParams();
  const { periodo, rango, setPeriodo, setRango } = usePeriodo();

  // El índice mide su alto contra el contenedor que scrollea (el <main> de
  // SidebarLayout), no contra la ventana: con una fórmula de svh el último
  // grupo —Administración— quedaba fuera del área visible y no había scroll
  // que lo alcanzara. Ver useStickyMaxHeight.
  const indiceRef = useRef(null);

  const { userInfo } = useAuth();
  const sinBitacora = esInversionista(userInfo);
  const grupos = gruposConReportes({ sinBitacora });
  // `top: 16` debe coincidir con el `top-4` del sticky.
  useStickyMaxHeight(indiceRef, { top: 16, modo: "fluir", deps: [grupos.length] });
  // Un link viejo a un reporte que ya no vive aquí (los de plataforma se
  // mudaron a Reportes admin) cae en el primero, no en una pantalla vacía.
  const pedido = reportePorId(searchParams.get("r"));
  const seleccionado =
    pedido && !(sinBitacora && pedido.requiereBitacora) ? pedido : REPORTES[0];

  const elegir = (id) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("r", id);
      return next;
    }, { replace: true });

  // Datos vivos del índice: el mes en curso (y el año para el estado de
  // resultados). Cálculo barato: todo ya está en memoria.
  const stats = useMemo(() => {
    const tz = calendarTimezone;
    // Los reportes con selector de período adelantan su cifra en ESE período
    // (el de la URL), no siempre en el mes: si se está viendo "Este año", el
    // índice no puede seguir hablando del mes. Los que traen controles
    // propios (año, hoy, lo que viene) dicen el suyo en el texto.
    const anio = getYearRange(new Date().getFullYear());
    const evts = resumenEventos({ events, costos, desde: rango.from, hasta: rango.to, timeZone: tz });
    const dineroAnio = resumenDinero({ payments, refunds, costos, depositos, retiros, desde: anio.from, hasta: anio.to, timeZone: tz });
    // Devengado del año transcurrido: solo eventos que ya ocurrieron.
    const evtsAnio = resumenEventos({ events, costos, desde: anio.from, hasta: new Date(), timeZone: tz });
    const leads = resumenLeads({ contactForms, desde: rango.from, hasta: rango.to, timeZone: tz });
    // El gasto del mes con la MISMA regla de fecha que usa el reporte
    // (`gastoPorProveedor` ancla por día calendario del venue), para que el
    // número del índice y el "Total del período" de adentro sean el mismo.
    const gastoMes = totalCostos(costos, {
      desde: rango.from,
      hasta: rango.to,
      timeZone: tz,
    });
    const inventario = resumenInventario({ conceptos, costos, desde: rango.from, hasta: rango.to, timeZone: tz });
    const estacionalidad = estacionalidadMensual({ events, costos, timeZone: tz });
    const mejorMes = estacionalidad
      .filter((m) => m.eventos > 0)
      .reduce((a, b) => (b.utilidad > (a?.utilidad ?? -Infinity) ? b : a), null);
    const cliente = resumenClientes({ clientes, events, payments, desde: rango.from, hasta: rango.to, timeZone: tz });
    const planner = resumenPlanners({ proveedores, costos, events, desde: rango.from, hasta: rango.to, timeZone: tz });
    const estructura = estructuraCostos({ costos, desde: rango.from, hasta: rango.to, timeZone: tz });
    const cartera = carteraPorCobrar({ events, timeZone: tz });
    const metodosMes = cajaPorMetodo({
      payments,
      refunds,
      costos,
      depositos,
      retiros,
      catalogo: metodosPagoConDefaultsLista,
      desde: rango.from,
      hasta: rango.to,
      timeZone: tz,
    }).metodos.filter((f) => f.ingresos > 0 && f.participacion !== null);
    const agenda = agendaVendida({ events, timeZone: tz });
    const ocupacion = rangoOcupacion(
      buildOccupiedSet(events),
      diaLocalDeAncla(rango.from),
      diaLocalDeAncla(rango.to, 1),
      "todos"
    );

    return {
      resumen: {
        texto: `Utilidad: ${formatMXN(evts.utilidad)}`,
        tone: evts.utilidad < 0 ? "text-red-600" : "text-emerald-600",
      },
      rentabilidad: {
        texto: `${evts.eventos} ${evts.eventos === 1 ? "evento" : "eventos"}`,
      },
      "estado-resultados": {
        texto: `Utilidad ${new Date().getFullYear()}: ${formatMXN(evtsAnio.utilidad)}`,
        tone: evtsAnio.utilidad < 0 ? "text-red-600" : "text-emerald-600",
      },
      "flujo-caja": {
        texto: `Flujo neto ${new Date().getFullYear()}: ${formatMXN(dineroAnio.flujoNeto)}`,
        tone: dineroAnio.flujoNeto < 0 ? "text-red-600" : "text-emerald-600",
      },
      cartera: {
        texto:
          cartera.vencido > 0
            ? `${formatMXN(cartera.vencido)} vencido`
            : cartera.total > 0
            ? `${formatMXN(cartera.total)} por vencer`
            : "Sin saldos vencidos",
        tone: cartera.vencido > 0 ? "text-red-600" : "",
      },
      "estructura-costos": {
        texto:
          estructura.pesoFijo === null
            ? "Sin costos en el período"
            : `${Math.round(estructura.pesoFijo * 100)}% del gasto es fijo`,
      },
      "agenda-vendida": {
        // Corto a propósito: el renglón del índice trunca, no parte en dos.
        texto:
          agenda.totales.valor > 0
            ? `Por venir: ${formatMXN(agenda.totales.valor)}`
            : "Sin eventos por venir",
        tone: agenda.totales.valor > 0 ? "text-emerald-600" : "",
      },
      estacionalidad: {
        texto: mejorMes
          ? `Mes de mayor utilidad: ${MONTHS_ES[mejorMes.mes]}`
          : "Sin historial suficiente",
        tone: mejorMes ? "text-emerald-600" : "",
      },
      comercial: {
        texto: `${leads.recibidos} ${leads.recibidos === 1 ? "lead" : "leads"}`,
      },
      clientes: {
        texto:
          cliente.totales.clientes === 0
            ? "Sin clientes"
            : `${cliente.totales.activos} con evento · ${cliente.totales.nuevos} ${
                cliente.totales.nuevos === 1 ? "nuevo" : "nuevos"
              }`,
      },
      planners: {
        texto:
          planner.totales.comisionTotal > 0
            ? `${formatMXN(planner.totales.comisionTotal)} en comisiones`
            : planner.filas.length > 0
            ? "Sin comisiones en el período"
            : "Sin planners en el directorio",
      },
      ocupacion: {
        texto: `${
          ocupacion.total > 0
            ? Math.round((ocupacion.ocupados / ocupacion.total) * 100)
            : 0
        }% ocupado`,
      },
      "proveedores-servicios": {
        // Antes decía lo por pagar de SIEMPRE, que adentro no aparece por
        // ningún lado (eso vive en Cartera): la puerta prometía una cifra
        // que el reporte no contestaba. El renglón del índice trunca, no
        // parte en dos, así que el texto se mantiene corto a propósito.
        texto:
          gastoMes > 0 ? `Gasto: ${formatMXN(gastoMes)}` : "Sin gasto en el período",
      },
      "metodos-pago": {
        texto: metodosMes.length
          ? `${metodosMes[0].nombre} ${Math.round(metodosMes[0].participacion * 100)}%`
          : "Sin ingresos en el período",
      },
      inventario: {
        texto:
          inventario.items.length > 0
            ? `${inventario.items.length} con piezas · ${formatMXN(inventario.totales.gastado)}`
            : "Sin servicios con piezas",
      },
      "uso-panel": {
        // Se carga bajo demanda (lee fuera de VenueProvider), así que el
        // índice no puede adelantar una cifra sin pagar esas lecturas.
        texto: "Tu equipo · últimos 30 días",
      },
    };
  }, [
    events,
    payments,
    refunds,
    costos,
    depositos,
    retiros,
    contactForms,
    conceptos,
    clientes,
    proveedores,
    metodosPagoConDefaultsLista,
    calendarTimezone,
    rango,
  ]);

  const { Component, Icon } = seleccionado;
  const conPeriodo = seleccionado.controles === "periodo";

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="mb-5">
        <div className="flex items-center gap-3">
          <ChartBarIcon className="h-6 w-6" style={{ color: ACCENT }} />
          <Heading level={2}>Reportes</Heading>
          {selectedVenue?.name && (
            <span className="text-zinc-400 hidden sm:inline">– {selectedVenue.name}</span>
          )}
        </div>
        <p className="text-sm text-zinc-500 mt-1">
          Cada reporte contesta una pregunta del negocio. Elígelo en el índice
          y pica las filas para ver de qué se compone cada cifra.
        </p>
      </div>

      <div className="flex gap-8">
        {/* Índice lateral — mismo patrón que la edición del venue: sticky,
            con scroll propio cuando la lista no cabe. */}
        <aside className="hidden lg:block w-60 shrink-0">
          {/* Sin overflow propio a propósito: o cabe completo y se queda
              pegado, o fluye con la página (ver useStickyMaxHeight "fluir").
              Dos barras de scroll compitiendo se ven rotas. */}
          <nav ref={indiceRef} className="sticky top-4 pr-1 space-y-4">
            {rango && (
              <div className="truncate text-xs text-zinc-500" title={rango.etiqueta}>
                Cifras de <span className="font-semibold" style={{ color: ACCENT }}>{rango.etiqueta}</span>
              </div>
            )}
            {grupos.map((grupo) => (
              <div key={grupo.id}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">
                  {grupo.label}
                </div>
                <ul className="space-y-0.5">
                  {grupo.reportes.map((r) => {
                    const activo = r.id === seleccionado.id;
                    const stat = stats[r.id];
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => elegir(r.id)}
                          aria-current={activo ? "page" : undefined}
                          className={`w-full text-left flex items-start gap-2 px-2 py-1 rounded-md transition-colors ${
                            activo
                              ? "bg-[#1b4d2e]/10 text-[#1b4d2e] dark:text-emerald-300"
                              : "text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                          }`}
                        >
                          <r.Icon
                            className={`h-4 w-4 mt-0.5 shrink-0 ${
                              activo ? "" : "text-zinc-400"
                            }`}
                            style={activo ? { color: ACCENT } : undefined}
                          />
                          <span className="min-w-0 flex-1 leading-tight">
                            <span
                              className={`block truncate text-sm ${
                                activo ? "font-semibold" : ""
                              }`}
                            >
                              {r.titulo}
                            </span>
                            {stat && (
                              <span
                                className={`block truncate text-[11px] ${
                                  stat.tone || "text-zinc-400"
                                }`}
                              >
                                {stat.texto}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          {/* En móvil el índice se colapsa a un select: trece renglones
              apilados empujarían el reporte fuera de la primera pantalla. */}
          <div className="lg:hidden mb-4">
            <label className="block text-xs font-semibold uppercase tracking-wide text-zinc-400 mb-1">
              Reporte
            </label>
            <select
              value={seleccionado.id}
              onChange={(e) => elegir(e.target.value)}
              className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
            >
              {grupos.map((grupo) => (
                <optgroup key={grupo.id} label={grupo.label}>
                  {grupo.reportes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.titulo}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm mb-6 overflow-hidden">
            <div className="flex items-start gap-3 p-5">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: ACCENT + "1a" }}
              >
                <Icon className="h-5 w-5" style={{ color: ACCENT }} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-zinc-900 dark:text-white">
                  {seleccionado.titulo}
                </h2>
                <p className="text-sm text-zinc-500 mt-0.5">{seleccionado.descripcion}</p>
              </div>
              {conPeriodo && rango && (
                <div className="hidden sm:block text-right shrink-0">
                  <div className="text-sm font-semibold" style={{ color: ACCENT }}>
                    {rango.etiqueta}
                  </div>
                  <div className="text-xs text-zinc-400">variaciones {rango.comparado}</div>
                </div>
              )}
            </div>
            {conPeriodo && (
              <div className="border-t border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 px-5 py-3">
                <PeriodoSelector
                  periodo={periodo}
                  setPeriodo={setPeriodo}
                  setRango={setRango}
                  rango={rango}
                />
              </div>
            )}
          </div>

          {/* Los depósitos se cargan UNA vez aquí (una lectura por proveedor)
              y viajan por prop: sin esto Resumen, Flujo y Estado los volvían
              a pedir cada uno. */}
          <Component rango={rango} depositos={depositos} />
        </main>
      </div>
    </div>
  );
};

export default ReportesPage;
